import {
  finalizeLabelText,
  hasInternalTokens,
  looksLikeOpaqueIdentifier,
  parseToolInvocation,
  toPlainText,
} from "./labelNormalizer";

const TOOL_NAME_LABELS: Record<string, string> = {
  get_stock_price_data: "Price data",
  get_options_market_data: "Options market data",
  get_vol_surface: "Volatility surface data",
  compute_ytd_return: "Year-to-date return data",
  compute_nday_return: "N-day return data",
  compute_drawdown: "Drawdown data",
  merge_timeseries_data: "Merged time series data",
  merge_time_series_data: "Merged time series data",
  compute_linear_regression: "Regression data",
  query_earnings_data: "Earnings data",
  query_earnings_calendar: "Earnings data",
};

const TRANSFORM_TOOL_NAMES = new Set([
  "transform_timeseries_data",
  "transform_time_series",
]);

const TRANSFORM_TYPE_LABELS: Record<string, string> = {
  moving_average: "Moving average",
  exponential_moving_average: "Exponential moving average",
  ema: "Exponential moving average",
  sma: "Moving average",
  rsi: "RSI",
  z_score: "Z-score",
  returns: "Returns",
  log_returns: "Log returns",
};

function normalizeSymbol(symbol?: string): string {
  if (!symbol) return "";
  const cleaned = symbol.replace(/['"]/g, "").trim();
  if (!cleaned || looksLikeOpaqueIdentifier(cleaned)) return "";
  return cleaned.toUpperCase();
}

function normalizeTransformType(transformType?: string): string {
  if (!transformType) return "Transformed";
  const key = transformType.trim().toLowerCase().replace(/\s+/g, "_");
  return TRANSFORM_TYPE_LABELS[key] || transformType.replace(/_/g, " ").trim();
}

function buildDateRange(startDate?: string, endDate?: string): string {
  if (startDate && endDate) {
    return `${startDate} to ${endDate}`;
  }
  if (startDate) {
    return `from ${startDate}`;
  }
  if (endDate) {
    return `until ${endDate}`;
  }
  return "";
}

function maybeBuildFromToolInvocation(rawDescription?: string): string {
  const parsed = parseToolInvocation(rawDescription);
  if (!parsed) return "";

  const { toolName, args } = parsed;
  const symbol = normalizeSymbol(args.symbol);
  const dateRange = buildDateRange(args.start_date, args.end_date);

  if (TRANSFORM_TOOL_NAMES.has(toolName)) {
    const transformType = normalizeTransformType(args.transform_type || args.type);
    const period = args.period || args.window;
    if (period) {
      return `${transformType} data (${period}-period)`;
    }
    return `${transformType} data`;
  }

  if (toolName === "compute_linear_regression") {
    const yField = args.y_field;
    if (yField && !looksLikeOpaqueIdentifier(yField)) {
      return `Regression data for ${yField}`;
    }
    return "Regression data";
  }

  const baseLabel = TOOL_NAME_LABELS[toolName];
  if (!baseLabel) {
    if (symbol) {
      return `Dataset for ${symbol}`;
    }
    return "";
  }

  let label = baseLabel;
  if (symbol) {
    label = `${label} for ${symbol}`;
  }
  if (dateRange) {
    label = `${label} (${dateRange})`;
  }

  return label;
}

function maybeBuildFromFreeText(rawDescription?: string): string {
  const plainText = toPlainText(rawDescription);
  if (!plainText) return "";

  if (hasInternalTokens(plainText)) return "";
  if (looksLikeOpaqueIdentifier(plainText)) return "";
  if (plainText.length < 4) return "";

  return plainText;
}

export function buildEnglishLabel(rawDescription?: string): string {
  const fromTool = maybeBuildFromToolInvocation(rawDescription);
  if (fromTool) return finalizeLabelText(fromTool);

  const fromText = maybeBuildFromFreeText(rawDescription);
  if (fromText) return finalizeLabelText(fromText);

  return "";
}
